import { forwardRef, Fragment, useImperativeHandle } from "react";

import { cn, platformProps } from "../../lib";
import { Box, Text, TextInput, type TextInputRef } from "../../primitives";
import { isComplete, joinDigits } from "./lib";
import {
  otpBoxVariants,
  otpCellClass,
  otpInputVariants,
  otpRowClass,
  otpSpacerVariants,
} from "./otp-input-field.variants";
import type { OTPInputFieldProps } from "./types";
import { useOTPInputController } from "./use-otp-input-controller";

/**
 * One file for web, iOS and Android (molecules audit 2026-10-30, OTPInputField
 * #8): both platforms render the same primitives through the same controller.
 */
export const OTPInputField = forwardRef<TextInputRef, OTPInputFieldProps>(
  (
    {
      length = 6,
      value,
      onChange,
      onComplete,
      inputType = "numeric",
      secure = false,
      state = "default",
      disabled = false,
      autoFocus = true,
      size = "md",
      gap = "default",
      showSeparator = false,
      separatorAfter = 3,
      placeholder = "",
      className,
      testID,
      accessibilityLabel = "One-time password",
    },
    ref,
  ) => {
    const {
      digits,
      focusedIndex,
      setFocusedIndex,
      inputRefs,
      setInputRef,
      handleInputValue,
      handleKeyPress,
    } = useOTPInputController<TextInputRef>({
      length,
      value,
      onChange,
      onComplete,
      inputType,
      disabled,
      autoFocus,
    });

    const spacer = <Box className={otpSpacerVariants({ gap })} />;

    // What screen readers hear, politely (OTPInputField #14): the result the
    // screen sets through `state` first, else that the code is complete.
    // English, as the "Digit N of L" labels, until i18n
    const status =
      state === "error"
        ? "Code incorrect"
        : state === "success"
          ? "Code accepted"
          : isComplete(joinDigits(digits), length)
            ? "Code complete"
            : "";

    useImperativeHandle(ref, () => inputRefs.current[0] as TextInputRef, [inputRefs]);

    return (
      <Box
        {...platformProps({ testID, slot: "otp-input-field" })}
        accessibilityLabel={accessibilityLabel}
        accessibilityRole="group"
        className={cn(otpRowClass, className)}
      >
        {Array.from({ length }).map((_, index) => {
          const char = digits[index];
          const hasValue = Boolean(char);
          const hasPlaceholder = Boolean(placeholder);
          const isFocused = focusedIndex === index;

          return (
            <Fragment key={`otp-${index}`}>
              {index > 0 ? spacer : null}
              {showSeparator && index > 0 && index === separatorAfter ? (
                <>
                  <Box className="w-spacing-md flex shrink-0 flex-col items-center justify-center">
                    <Text className="text-luce-neutral-foreground-attenuated">-</Text>
                  </Box>
                  {spacer}
                </>
              ) : null}
              <Box className={otpCellClass}>
                <Box
                  className={otpBoxVariants({
                    size,
                    state,
                    disabled,
                    focused: isFocused,
                    filled: hasValue,
                  })}
                >
                  <TextInput
                    ref={(node) => {
                      setInputRef(index, node);
                    }}
                    value={char}
                    onFocus={() => setFocusedIndex(index)}
                    // Focus leaving the code clears its focused look
                    onBlur={() => setFocusedIndex((current) => (current === index ? -1 : current))}
                    // Typing into a filled box replaces its digit
                    selectTextOnFocus
                    onChangeText={(text) => handleInputValue(index, text)}
                    // Web: the whole clipboard text, before `maxLength` truncates
                    // it. The native primitive ignores it (a paste arrives through
                    // onChangeText)
                    onPasteText={(text) => handleInputValue(index, text, { paste: true })}
                    onKeyDown={(event) => handleKeyPress(index, event.key)}
                    disabled={disabled}
                    // Web: aria-invalid; React Native has no invalid state
                    invalid={state === "error"}
                    // No maxLength: a paste or autofill reaches handleInputValue
                    // whole, separators included, and it fills the boxes
                    autoCorrect={false}
                    keyboardType={inputType === "numeric" ? "number-pad" : "default"}
                    secureTextEntry={secure}
                    // RN derives iOS `textContentType="oneTimeCode"` from it, and the
                    // primitive opts it into Android autofill
                    autoComplete="one-time-code"
                    // The variant's `placeholder:` class colours the placeholder, and
                    // the primitive resets Android's padding
                    className={otpInputVariants({
                      size,
                      disabled,
                      tone: !hasValue && hasPlaceholder ? "placeholder" : "default",
                    })}
                    placeholder={char ? "" : placeholder}
                    accessibilityLabel={`Digit ${index + 1} of ${length}`}
                  />
                </Box>
              </Box>
            </Fragment>
          );
        })}
        {/* Read by screen readers only, always mounted so a change is
            announced (web, Android; iOS has no live regions). sr-only's -1px
            margin would put it outside the row, where VoiceOver and TalkBack
            drop it, so native keeps it inside with transparent text */}
        <Text
          className="native:m-0 native:text-transparent sr-only"
          {...platformProps({
            testID: testID ? `${testID}-status` : undefined,
            liveRegion: "polite",
          })}
        >
          {status}
        </Text>
      </Box>
    );
  },
);

OTPInputField.displayName = "OTPInputField";
